import React, { useState } from 'react';
import { FileText, Loader2, UploadCloud } from 'lucide-react';
import { uploadFileInChunks } from '../../utils/chunkedUpload';

const MAX_MB = 200;

const ManuscriptReplacer = ({ book, bookId, token, onReplaced }) => {
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [status, setStatus] = useState('');
  const [msg, setMsg] = useState(null);

  const current = book?.book_file_url;
  const label = book?.book_file_name
    || (book?.book_file_type?.includes('epub') ? 'EPUB manuscript' : current ? 'PDF manuscript' : null);

  const replace = async (file) => {
    if (!file) return;
    const ext = (file.name || '').toLowerCase().split('.').pop();
    if (!['pdf', 'epub'].includes(ext)) { setMsg('Manuscript must be a PDF or EPUB file.'); return; }
    if (file.size > MAX_MB * 1024 * 1024) { setMsg(`Manuscript must be under ${MAX_MB}MB.`); return; }
    setUploading(true);
    setMsg(null);
    setProgress(0);
    try {
      await uploadFileInChunks(file, {
        fileType: 'book',
        bookId,
        token,
        label: 'manuscript',
        onProgress: (pct, text) => { setProgress(pct); setStatus(text); },
      });
      setMsg('Manuscript replaced! Reader pages and AI reading context are regenerating in the background.');
      onReplaced?.();
    } catch (e) {
      setMsg(e.response?.data?.detail || e.message || 'Upload failed — please try again.');
    } finally {
      setUploading(false);
      setStatus('');
    }
  };

  return (
    <>
      <div className="border-t border-white/10 pt-4 mt-4">
        <h4 className="text-sm uppercase tracking-wider text-gray-400 mb-3">Manuscript</h4>
      </div>
      <div className="flex items-start gap-4" data-testid="manuscript-section">
        <div className="w-20 h-[120px] rounded-lg bg-white/5 border border-white/10 flex items-center justify-center flex-shrink-0">
          <FileText className={`h-6 w-6 ${current ? 'text-rose-400' : 'text-gray-600'}`} />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-xs text-gray-400 mb-2 truncate" data-testid="manuscript-current">
            {label ? <>Current: <span className="text-gray-200">{label}</span></> : 'No manuscript uploaded yet.'}
          </p>
          <input
            type="file"
            accept=".pdf,.epub"
            id="replace-manuscript-input"
            className="hidden"
            disabled={uploading}
            data-testid="replace-manuscript-input"
            onChange={(e) => { replace(e.target.files?.[0]); e.target.value = ''; }}
          />
          <label
            htmlFor="replace-manuscript-input"
            className={`inline-flex items-center gap-1.5 text-sm font-medium bg-white/10 hover:bg-white/15 text-gray-200 rounded-full px-4 py-2 transition-colors ${uploading ? 'opacity-60 cursor-wait' : 'cursor-pointer'}`}
            data-testid="replace-manuscript-btn"
          >
            {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <UploadCloud className="h-4 w-4" />}
            {current ? 'Replace Manuscript' : 'Add Manuscript'}
          </label>
          <p className="text-xs text-gray-500 mt-2">
            PDF or EPUB, up to {MAX_MB}MB. The old file is removed and the reader, page images and AI reading context rebuild from the new one.
          </p>
          {uploading && (
            <div className="mt-2" data-testid="manuscript-progress">
              <div className="h-1.5 w-full bg-white/10 rounded-full overflow-hidden">
                <div className="h-full bg-rose-500 transition-[width] duration-300" style={{ width: `${progress}%` }} />
              </div>
              <p className="text-[11px] text-gray-400 mt-1">{status}</p>
            </div>
          )}
          {msg && (
            <p
              className={`text-xs mt-1.5 ${msg.startsWith('Manuscript replaced') ? 'text-emerald-400' : 'text-red-400'}`}
              data-testid="manuscript-upload-msg"
            >
              {msg}
            </p>
          )}
        </div>
      </div>
    </>
  );
};

export default ManuscriptReplacer;
